import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getConversations } from '../lib/api-client';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import AnimationLayer from '../components/desktop/AnimationLayer';
import { getThemeAnimations } from '@theme/animation-config';
import { Search, Plus, Send, Users, Edit3, X, Smile, Paperclip, Bell, Heart, MessageSquare, MoreVertical, Trash2, EyeOff, LogOut, ChevronDown } from 'lucide-react';
import { PixelatedTurtle } from '@theme/design-constants';
import { useScale, scaleW, scaleH } from '../hooks/useScale';
import LoadingScreen from '../components/desktop/LoadingScreen';
import { useAuth } from '../context/AuthContext';
import MainNavbar from '../components/desktop/MainNavbar';
import { getLegacyPostUrl, getCommunityPostUrl, getForumPostUrl } from '../utils/postUrls';
import { notificationEventBus } from '../lib/notificationEventBus';
import { useNotificationSync } from '../hooks/useNotificationSync';
import { getBackground, getCardBackground, calculateNavbarBg } from '../utils/backgroundHelpers';
import resolveAvatar from '../lib/resolveAvatar';
import { getCachedTheme } from '../utils/themeCache';

import { fetchWithAuth } from '../lib/fetchWithAuth';
type User = {
  id: string;
  username: string;
  displayName?: string;
  avatar?: string;
  profileTitle?: string;
  userStatus?: string;
  profileFrameShape?: 'square' | 'circle';
  profileFrameDesign?: 'coral' | 'waves' | 'bubbles';
  profileDecoration?: string;
};

type Conversation = {
  id: string;
  name?: string | null;
  type: 'dm' | 'group';
  participants: User[];
  lastMessage?: {
    text: string;
    from: string;
    at: string;
  };
  updatedAt: string;
};

type Message = {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  timestamp: string;
  isOwn: boolean;
};

export default function Messages() {
  const navigate = useNavigate();
  const { theme, currentPreset, siteCustomization, siteAnimations, ensureSiteThemeApplied } = useTheme();
  const { user: authUser, loading: authLoading, logout, setShowLoginModal } = useAuth();
  const { setCurrentTheme } = useNavigation();
  const scale = useScale();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const currentUser = authUser;
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingStatus, setEditingStatus] = useState(false);
  const [statusInput, setStatusInput] = useState('');
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [newConvSearchQuery, setNewConvSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [groupChatTitle, setGroupChatTitle] = useState('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaType, setMediaType] = useState<'image' | 'video'>('image');
  const [themeAnimations, setThemeAnimations] = useState(() => {
    // UNIFIED CACHE: Use animations from ThemeContext
    if (siteAnimations) {
      return siteAnimations;
    }
    // Fallback to preset animations
    return getThemeAnimations(currentPreset);
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Use theme from ThemeContext directly (already has computedColors applied)
  const localTheme = React.useMemo(() => {
        return {
      ...theme,
      textColor: theme.textColor!,
      accentColor: theme.accentColor!,
      primaryColor: theme.primaryColor!,
      secondaryColor: theme.secondaryColor!,
      cardBg: theme.cardBg,
      cardGradientColor1: theme.cardGradientColor1,
      cardGradientColor2: theme.cardGradientColor2,
      cardGradientEnabled: theme.cardGradientEnabled,
      navbarBg: theme.navbarBg!,
      tabBarBg: theme.tabBarBg!,
    };
  }, [theme]);

  const themeColors = {
    navbarBg: localTheme.navbarBg!,
    accentColor: localTheme.accentColor!,
    textColor: localTheme.textColor!,
    tabBarBg: localTheme.tabBarBg!,
    gradientFrom: localTheme.primaryColor!,
    gradientTo: localTheme.secondaryColor!
  };


  // Register site theme with NavigationContext - ONLY when theme fully loads
  // CRITICAL: Do NOT register on mount, or it will overwrite the previous page's theme
  // before LoadingScreen can read it
  // Register site theme with NavigationContext - ONLY AFTER loading is complete
  // CRITICAL: Do NOT call setCurrentTheme during loading, or it will overwrite the previous page's theme
  // and the loading screen will show site colors instead of profile/community colors
  useEffect(() => {
    if (loading) return;
    if (!siteCustomization) {
      // Site theme was fetched while a profile/community page was active — apply it now.
      ensureSiteThemeApplied();
      return;
    }
    if (theme?.primaryColor) {
      console.log('[Messages] 🎨 Setting currentTheme for loading screen (after loading complete):');
      setCurrentTheme({
        type: 'site',
        colors: {
          primaryColor: theme.primaryColor,
          secondaryColor: theme.secondaryColor || theme.primaryColor,
          textColor: theme.textColor || '#ffffff',
          accentColor: theme.accentColor || theme.primaryColor
        }
      });
    }
  }, [loading, theme?.primaryColor, theme?.secondaryColor, theme?.textColor, theme?.accentColor, siteCustomization, setCurrentTheme, ensureSiteThemeApplied]);

  // Sync animations when siteAnimations or currentPreset changes
  useEffect(() => {
    if (siteAnimations) {
      setThemeAnimations(siteAnimations);
    } else {
      setThemeAnimations(getThemeAnimations(currentPreset));
    }
  }, [siteAnimations, currentPreset]);

  // Listen for theme updates from customizer for animations only
  useEffect(() => {
    const handleThemeUpdate = (event: any) => {
      const { animations, animationsEnabled: enabled } = event.detail;

      // Fix and validate animations object structure before updating
      if (animations) {
        const fixedAnimations = { ...animations };

        // Fix incomplete bubbles structure
        if (fixedAnimations.bubbles && typeof fixedAnimations.bubbles === 'object') {
          if (!fixedAnimations.bubbles.id || !fixedAnimations.bubbles.type || !fixedAnimations.bubbles.count) {
            // If bubbles is incomplete, create proper structure based on enabled state
            fixedAnimations.bubbles = fixedAnimations.bubbles.enabled
              ? {
                  id: 'bubbles',
                  type: 'rising',
                  enabled: true,
                  count: 25,
                  speed: 0.8,
                  size: { min: 15, max: 35 },
                }
              : null;
          }
        }

        // Basic structure validation
        const hasValidStructure =
          fixedAnimations.characters && Array.isArray(fixedAnimations.characters) &&
          fixedAnimations.decorations && Array.isArray(fixedAnimations.decorations) &&
          fixedAnimations.characters.length > 0 &&
          fixedAnimations.characters[0]?.id;

        if (hasValidStructure) {
          setThemeAnimations(fixedAnimations);
        }
      }
    };

    window.addEventListener('siteThemeUpdated', handleThemeUpdate);
    return () => window.removeEventListener('siteThemeUpdated', handleThemeUpdate);
  }, []);

  // Helper to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
  };

  // Apply theme as CSS variables when localTheme changes
  useEffect(() => {
    const root = document.documentElement;

    // Helper to blend two colors
    const blendColors = (color1: string, color2: string, ratio: number) => {
      const r1 = parseInt(color1.slice(1, 3), 16);
      const g1 = parseInt(color1.slice(3, 5), 16);
      const b1 = parseInt(color1.slice(5, 7), 16);

      const r2 = parseInt(color2.slice(1, 3), 16);
      const g2 = parseInt(color2.slice(3, 5), 16);
      const b2 = parseInt(color2.slice(5, 7), 16);

      const r = Math.round(r1 * (1 - ratio) + r2 * ratio);
      const g = Math.round(g1 * (1 - ratio) + g2 * ratio);
      const b = Math.round(b1 * (1 - ratio) + b2 * ratio);

      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    // Apply background gradient
    if ((localTheme as any).gradientEnabled) {
      const ratio = ((localTheme as any).gradientFavorability || 50) / 100;
      const blendedColor1 = blendColors(localTheme.primaryColor!, localTheme.secondaryColor!, ratio);
      const blendedColor2 = blendColors(localTheme.secondaryColor!, localTheme.primaryColor!, ratio);
      const strength = ((localTheme as any).gradientStrength || 100) / 100;
      const finalColor1 = blendColors('#808080', blendedColor1, strength);
      const finalColor2 = blendColors('#808080', blendedColor2, strength);

      root.style.setProperty('--app-bg', `linear-gradient(${(localTheme as any).gradientAngle || 135}deg, ${finalColor1}, ${finalColor2})`);
    } else {
      root.style.setProperty('--app-bg', localTheme.primaryColor!);
    }

    // Apply other CSS variables (using transformed colors from localTheme)
    root.style.setProperty('--color-text', localTheme.textColor!);
    root.style.setProperty('--color-accent', localTheme.accentColor!);
    root.style.setProperty('--navbar-bg', localTheme.navbarBg!);
    root.style.setProperty('--tab-bar-bg', localTheme.tabBarBg!);

    // Apply card background with gradient if enabled
    if (localTheme.cardGradientEnabled) {
      const ratio = ((localTheme as any).cardGradientFavorability || 50) / 100;
      const blendedCard1 = blendColors(localTheme.cardGradientColor1!, localTheme.cardGradientColor2!, ratio);
      const blendedCard2 = blendColors(localTheme.cardGradientColor2!, localTheme.cardGradientColor1!, ratio);
      const strength = ((localTheme as any).cardGradientStrength || 100) / 100;
      const finalCard1 = blendColors(localTheme.cardBg, blendedCard1, strength);
      const finalCard2 = blendColors(localTheme.cardBg, blendedCard2, strength);

      const gradientBg = `linear-gradient(${(localTheme as any).cardGradientAngle || 120}deg, ${hexToRgba(finalCard1, (localTheme as any).cardOpacity || 100)}, ${hexToRgba(finalCard2, (localTheme as any).cardOpacity || 100)})`;
      root.style.setProperty('--card-bg', gradientBg);
    } else {
      root.style.setProperty('--card-bg', hexToRgba(localTheme.cardBg, (localTheme as any).cardOpacity || 100));
    }
  }, [localTheme]);

  // Update animations when theme changes
  useEffect(() => {
    setThemeAnimations(getThemeAnimations(currentPreset));
  }, [currentPreset]);

  // Site theme is already loaded by ThemeContext

  // Theme updates are handled automatically by ThemeContext

  // Notifications
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // Menus
  const [showConversationMenu, setShowConversationMenu] = useState<string | null>(null);
  const [showMessageMenu, setShowMessageMenu] = useState<string | null>(null);

  // Hidden conversations (stored in localStorage)
  const [hiddenConversations, setHiddenConversations] = useState<string[]>(() => {
    const stored = localStorage.getItem('hiddenConversations');
    return stored ? JSON.parse(stored) : [];
  });

  const emojis = ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '🥲', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '😶‍🌫️', '🥴', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕', '😟', '🙁', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '👍', '👎', '👏', '🙌', '👐', '🤝', '🙏', '✌️', '🤞', '🤟', '🤘', '🤙', '👋', '🤚', '✋', '🖐️', '👌', '🤌', '🤏', '✊', '👊', '🤛', '🤜', '💪', '🦾', '🌊', '🐢', '🐠', '🐟', '🐡', '🦈', '🐙', '🦑', '🦀', '🦞', '🦐', '🐚', '🪸', '⭐', '🌟', '✨', '💫', '🌈', '☀️', '🌤️', '⛅', '🌥️', '☁️', '🌦️', '🌧️', '⛈️', '🌩️', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❤️‍🔥', '❤️‍🩹', '💕', '💞', '💓', '💗', '💖', '💘', '💝'];

  // Scroll to bottom helper
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const navItems = [
    { label: 'Home', path: '/feed' },
    { label: 'Friends', path: '/friends' },
    { label: 'Messages', path: '/messages' },
    { label: 'Community', path: '/community' },
    { label: 'Ads', path: '/ads' },
    { label: 'Marketplace', path: '/marketplace' }
  ];

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'FOLLOW':
        return <Users size={20} style={{ color: localTheme.textColor }} />;
      case 'NEW_FRIEND':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
        return <MessageSquare size={20} style={{ color: localTheme.textColor }} />;
      case 'PROFILE_POST':
        return <Edit3 size={20} style={{ color: localTheme.textColor }} />;
      case 'LIKE':
        return <Heart size={20} style={{ color: localTheme.textColor }} />;
      default:
        return <Bell size={20} style={{ color: localTheme.textColor }} />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    const actor = notification.actorName || 'Someone';
    switch (notification.type) {
      case 'FOLLOW':
        return `${actor} started following you`;
      case 'NEW_FRIEND':
        return `You and ${actor} are now friends!`;
      case 'REPLY':
        return `${actor} replied to your post`;
      case 'COMMUNITY_REPLY':
        return `${actor} replied to your community post`;
      case 'PROFILE_POST':
        return `${actor} posted on your profile`;
      case 'LIKE':
        return `${actor} liked your post`;
      default:
        return notification.content || 'New notification';
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: any) => {
    setShowNotifications(false);

    switch (notification.type) {
      case 'FOLLOW':
      case 'NEW_FRIEND':
        if (notification.actorUsername) {
          navigate(`/u/${notification.actorUsername}`);
        } else if (notification.actorId) {
          navigate(`/user/${notification.actorId}`);
        }
        break;
      case 'REPLY':
      case 'COMMUNITY_REPLY':
      case 'LIKE':
      case 'DISLIKE':
      case 'REPOST':
        if (notification.postType === 'forum' && notification.communitySlug && notification.postSlug) {
          navigate(getForumPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'community' && notification.communitySlug && notification.postSlug) {
          navigate(getCommunityPostUrl(notification.communitySlug, notification.postSlug));
        } else if (notification.postType === 'legacy' && notification.postAuthorUsername && notification.postSlug) {
          navigate(getLegacyPostUrl(notification.postAuthorUsername, notification.postSlug));
        } else {
          navigate('/feed');
        }
        break;
      case 'PROFILE_POST':
        navigate('/user');
        break;
      case 'MESSAGE':
        navigate('/messages');
        break;
    }
  };

  // PixelatedTurtle is now imported from @theme/design-constants

  useEffect(() => {
    async function loadData() {
      // Track when data loading started
      const loadStartTime = Date.now();

      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      // If not logged in after auth loads, don't fetch data
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const convos = await getConversations();
        // Map API response to match local Conversation type
        const mappedConvos: Conversation[] = convos.map((convo: any) => ({
          id: convo.id,
          name: convo.name,
          type: convo.type,
          participants: (convo.participants || []).map((p: any) => ({
            id: p.id.toString(),
            username: p.username,
            displayName: p.name || p.username,
            avatar: p.profilePicture || undefined,
            profileTitle: p.profileTitle,
            userStatus: p.userStatus,
            profileFrameShape: p.profileFrameShape,
            profileFrameDesign: p.profileFrameDesign,
          })),
          lastMessage: convo.lastMessage ? {
            text: convo.lastMessage.text || convo.lastMessage.content,
            from: convo.lastMessage.from || convo.lastMessage.senderName || 'Unknown',
            at: convo.lastMessage.at || convo.lastMessage.createdAt,
          } : undefined,
          updatedAt: convo.updatedAt,
        }));
        setConversations(mappedConvos);

        // Fetch friends to populate available users for new conversations
        try {
          const { getFriends } = await import('../lib/api-client');
          const friendsData = await getFriends();
          setAvailableUsers(friendsData);
        } catch (friendsError) {
          // If getFriends fails, set empty array
          setAvailableUsers([]);
        }
      } catch (error) {
        // Don't navigate to login - let Layout handle auth redirects
      } finally {
        // Ensure loading screen stays visible for at least 200ms to show previous page's theme
        const loadTime = Date.now() - loadStartTime;
        const minLoadTime = 200;
        if (loadTime < minLoadTime) {
          setTimeout(() => {
            setLoading(false);
          }, minLoadTime - loadTime);
        } else {
          setLoading(false);
        }
      }
    }

    loadData();
  }, [currentUser, authLoading]);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();
  }, []);

  // Notification synchronization for real-time updates
  useNotificationSync({
    onNew: (notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },
    onRead: (notificationId) => {
      setNotifications(prev => prev.map(n => n.id === notificationId ? {...n, read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    },
    onReadAll: () => {
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
    },
    onDelete: (notificationId) => {
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    },
  });

  // Reload messages when conversation is selected
  useEffect(() => {
    if (!selectedConversation || !currentUser) return;

    const loadMessages = async () => {
      try {
        const response = await fetchWithAuth(`/api/conversations/${selectedConversation.id}/messages`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          const messagesData = data.messages || data || [];

          // Validate that messagesData is an array
          if (!Array.isArray(messagesData)) {
            setMessages([]);
            return;
          }

          const formattedMessages: Message[] = messagesData.map((msg: any) => ({
            id: msg.id,
            text: msg.text || msg.content,
            senderId: String(msg.fromUserId || msg.senderId),
            senderName: msg.fromUser?.profileName || msg.fromUser?.name || msg.fromUser?.username || msg.sender?.displayName || msg.sender?.username || msg.senderName || 'Unknown',
            timestamp: msg.at || msg.createdAt,
            isOwn: currentUser ? String(msg.fromUserId || msg.senderId) === String(currentUser.id) : false
          }));
          setMessages(formattedMessages);
        }
      } catch (error) {
      }
    };

    loadMessages();
  }, [selectedConversation, currentUser]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSelectConversation = async (conv: Conversation) => {
    setSelectedConversation(conv);

    // Fetch real messages for the conversation
    try {
      const response = await fetchWithAuth(`/api/conversations/${conv.id}/messages`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const messagesData = data.messages || data || [];

        if (!Array.isArray(messagesData)) {
          setMessages([]);
          return;
        }

        const formattedMessages: Message[] = messagesData.map((msg: any) => ({
          id: msg.id,
          text: msg.text || msg.content, // Support both field names
          senderId: String(msg.fromUserId || msg.senderId),
          senderName: msg.fromUser?.profileName || msg.fromUser?.name || msg.fromUser?.username || msg.sender?.displayName || msg.sender?.username || msg.senderName || 'Unknown',
          timestamp: msg.at || msg.createdAt,
          isOwn: currentUser ? String(msg.fromUserId || msg.senderId) === String(currentUser.id) : false
        }));

        setMessages(formattedMessages);
      } else {
        setMessages([]);
      }
    } catch (error) {
      setMessages([]);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const messageText = newMessage;
    setNewMessage(''); // Clear input immediately for better UX

    try {
      const response = await fetchWithAuth('/api/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          conversationId: selectedConversation.id,
          text: messageText
        })
      });

      if (response.ok) {
        const data = await response.json();
        // API returns: { message: { id, conversationId, fromUserId, text, at } }
        const newMsg: Message = {
          id: data.message.id,
          text: data.message.text,
          senderId: currentUser ? String(currentUser.id) : '',
          senderName: currentUser ? (currentUser.profileName || currentUser.name || currentUser.username || 'Unknown') : 'Unknown',
          timestamp: data.message.at,
          isOwn: true
        };
        setMessages([...messages, newMsg]);

        // Unhide conversation if it was hidden
        if (hiddenConversations.includes(selectedConversation.id)) {
          const newHidden = hiddenConversations.filter(id => id !== selectedConversation.id);
          setHiddenConversations(newHidden);
          localStorage.setItem('hiddenConversations', JSON.stringify(newHidden));
        }

        // Update the conversation in the list to show the new last message
        setConversations(conversations.map(conv => {
          if (conv.id === selectedConversation.id) {
            return {
              ...conv,
              lastMessage: {
                text: data.message.text,
                from: currentUser?.displayName || currentUser?.name || currentUser?.username || 'Unknown',
                at: data.message.at
              },
              updatedAt: data.message.at
            } as any;
          }
          return conv;
        }).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));
      } else {
        const errorText = await response.text();
        setNewMessage(messageText); // Restore message on error
      }
    } catch (error) {
      setNewMessage(messageText); // Restore message on error
    }
  };

  const handleAddMedia = () => {
    if (!mediaUrl.trim()) return;

    const mediaMessage: Message = {
      id: Date.now().toString(),
      text: mediaUrl,
      senderId: currentUser ? String(currentUser.id) : '',
      senderName: currentUser ? (currentUser.displayName || currentUser.username) : 'Unknown',
      timestamp: new Date().toISOString(),
      isOwn: true
    };

    setMessages([...messages, mediaMessage]);
    setMediaUrl('');
    setShowMediaModal(false);
  };

  const handleUpdateStatus = async () => {
    if (!currentUser) return;

    try {
      const response = await fetchWithAuth(`/api/profile/${currentUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userStatus: statusInput || null
        })
      });

      if (response.ok) {
        // Status updated successfully
        setEditingStatus(false);
        // Update the current user's status in state instead of reloading
        if (currentUser) {
          (currentUser as any).userStatus = statusInput || null;
        }
      } else {
        alert('Failed to update status. Please try again.');
      }
    } catch (error) {
      alert('Failed to update status. Please try again.');
    }
  };

  const handleToggleUserSelection = (user: User) => {
    if (selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    } else if (selectedUsers.length < 20) {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleHideConversation = (convId: string) => {
    const newHidden = [...hiddenConversations, convId];
    setHiddenConversations(newHidden);
    localStorage.setItem('hiddenConversations', JSON.stringify(newHidden));
    setShowConversationMenu(null);

    // If hiding the selected conversation, clear selection
    if (selectedConversation?.id === convId) {
      setSelectedConversation(null);
      setMessages([]);
    }
  };

  const handleLeaveConversation = async (conv: Conversation) => {
    try {
      // Send leave message
      const leaveMessage = `${currentUser ? (currentUser.displayName || currentUser.username) : 'User'} has left the conversation`;
      await fetchWithAuth(`/api/conversations/${conv.id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: leaveMessage })
      });

      // Remove from local list
      setConversations(conversations.filter(c => c.id !== conv.id));
      setShowConversationMenu(null);

      // If leaving the selected conversation, clear selection
      if (selectedConversation?.id === conv.id) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      alert('Failed to leave conversation');
    }
  };

  const handleStartConversation = async () => {
    if (selectedUsers.length === 0) return;

    try {
      // Create conversation on server - must include current user in participantIds
      const participantIds = [currentUser ? (typeof currentUser.id === 'number' ? currentUser.id : parseInt(currentUser.id)) : 0, ...selectedUsers.map(u => typeof u.id === 'number' ? u.id : parseInt(String(u.id)))];
      const response = await fetchWithAuth('/api/conversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type: selectedUsers.length > 1 ? 'group' : 'dm',
          participantIds,
          name: selectedUsers.length > 1 ? (groupChatTitle || undefined) : undefined
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error('Failed to create conversation');
      }

      const data = await response.json();

      // API returns { id, reused } not { conversation: {...} }
      const newConversation: Conversation = {
        id: data.id,
        type: selectedUsers.length > 1 ? 'group' : 'dm',
        name: selectedUsers.length > 1 ? (groupChatTitle || undefined) : undefined,
        participants: (currentUser ? [currentUser, ...selectedUsers] : [...selectedUsers]) as any,
        messages: [],
        lastMessage: undefined,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as any;

      setConversations([newConversation, ...conversations]);
      setShowNewConversationModal(false);
      setSelectedUsers([]);
      setGroupChatTitle('');
      setNewConvSearchQuery('');

      // Fetch the conversation with welcome message from backend
      await handleSelectConversation(newConversation);
    } catch (error) {
      alert('Failed to create conversation. Please try again.');
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d`;

    return date.toLocaleDateString();
  };

  const formatFullDateTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getRecipientUser = () => {
    if (!selectedConversation || selectedConversation.type === 'group' || !currentUser) return null;
    return selectedConversation.participants.find((p: any) => String(p.id) !== String(currentUser?.id));
  };

  const handleLogout = async () => {
    logout();
  };

  const filteredConversations = conversations.filter(conv => {
    // Filter out hidden conversations
    if (hiddenConversations.includes(conv.id)) return false;

    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();

    if (conv.name) return conv.name.toLowerCase().includes(query);

    return conv.participants.some(p =>
      p.displayName?.toLowerCase().includes(query) ||
      p.username.toLowerCase().includes(query)
    );
  });

  const handleToggleNewConversationModal = useCallback(() => {
    setShowNewConversationModal(prev => !prev);
  }, []);

  const handleCloseNewConversationModal = useCallback(() => {
    setShowNewConversationModal(false);
  }, []);

  const handleCloseConversationMenu = useCallback(() => {
    setShowConversationMenu(null);
  }, []);

  const handleCloseMessageMenu = useCallback(() => {
    setShowMessageMenu(null);
  }, []);

  const handleToggleMediaModal = useCallback(() => {
    setShowMediaModal(prev => !prev);
  }, []);

  const handleCloseMediaModal = useCallback(() => {
    setShowMediaModal(false);
  }, []);

  const handleToggleEmojiPicker = useCallback(() => {
    setShowEmojiPicker(prev => !prev);
  }, []);

  const handleCloseEmojiPicker = useCallback(() => {
    setShowEmojiPicker(false);
  }, []);

  const handleSetMediaTypeImage = useCallback(() => {
    setMediaType('image');
  }, []);

  const handleSetMediaTypeVideo = useCallback(() => {
    setMediaType('video');
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  // Type guard: Messages page requires authentication
  if (!currentUser) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen w-full relative overflow-x-hidden" style={{ background: getBackground(localTheme as any, {}, theme.appBg), color: localTheme.textColor }}>
      <style>{`
        .pixelated {
          image-rendering: pixelated;
          image-rendering: -moz-crisp-edges;
          image-rendering: crisp-edges;
        }
        .thought-bubble {
          position: relative;
          background: ${localTheme.accentColor}20;
          border: 2px solid ${localTheme.accentColor};
          border-radius: 20px;
          padding: 12px 16px;
          margin-top: 12px;
        }
        .thought-bubble:before,
        .thought-bubble:after {
          content: '';
          position: absolute;
          width: 12px;
          height: 12px;
          background: ${localTheme.accentColor}20;
          border: 2px solid ${localTheme.accentColor};
          border-radius: 50%;
          bottom: -16px;
          left: 20px;
        }
        .thought-bubble:after {
          width: 8px;
          height: 8px;
          bottom: -24px;
          left: 14px;
        }
      `}</style>

      <AnimationLayer
        animations={themeAnimations}
        isMobile={false}
        bubbleColor={(localTheme as any).bubbleColor || localTheme.accentColor}
        turtleColor={(localTheme as any).turtleColor || localTheme.accentColor}
        zIndex={0}
      />

      {/* Top Navigation Bar */}
      <MainNavbar
        currentPath="/messages"
        onLogout={handleLogout}
        onSignIn={() => setShowLoginModal(true)}
        currentUser={currentUser}
        notifications={notifications}
        unreadCount={unreadCount}
        theme={localTheme}
        customization={localTheme as any}
        navbarBg={localTheme.navbarBg}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onNotificationClick={handleNotificationClick}
        getNotificationIcon={getNotificationIcon}
        getNotificationMessage={getNotificationMessage}
        formatNotificationTime={formatNotificationTime}
        setNotifications={setNotifications}
        setUnreadCount={setUnreadCount}
      />

      {/* 3-Column Layout */}
      <div className="flex h-[calc(100vh-80px)] gap-4 p-6">
        {/* Left Card - Conversations List (33vw) */}
        <div className="rounded-lg backdrop-blur-sm border overflow-hidden flex flex-col" style={{
          width: '33vw',
          background: getCardBackground(localTheme as any, {}, theme.cardBg),
          borderColor: localTheme.accentColor + '40'
        }}>
          {/* Search Header */}
          <div className="p-4" style={{ borderBottom: `1px solid ${localTheme.accentColor}40` }}>
            <div className="relative flex items-center">
              <Search size={18} className="absolute left-3" style={{ color: localTheme.textColor + '60', pointerEvents: 'none', zIndex: 10 }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="w-full py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                style={{
                  paddingLeft: '2.5rem',
                  paddingRight: '1rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderColor: localTheme.accentColor + '40',
                  color: localTheme.textColor
                }}
              />
            </div>
            <button
              onClick={handleToggleNewConversationModal}
              className="w-full mt-3 px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90"
              style={{
                backgroundColor: localTheme.accentColor,
                color: localTheme.textColor
              }}
            >
              <Plus size={18} />
              New Conversation
            </button>
          </div>

          {/* Conversations Scrollview */}
          <div className="flex-1 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <PixelatedTurtle size={60} />
                <p className="mt-4" style={{ color: localTheme.textColor + '99' }}>
                  {searchQuery ? 'No conversations found' : 'No conversations yet'}
                </p>
              </div>
            ) : (
              <div className="space-y-2 p-2">
                {filteredConversations.map((conv) => {
                  const isActive = selectedConversation?.id === conv.id;
                  const currentUserId = currentUser?.id ? String(currentUser.id) : null;
                  const title = conv.name || conv.participants.find((p: any) => String(p.id) !== currentUserId)?.displayName || 'Unknown';
                  const avatar = conv.type === 'dm' ? conv.participants.find((p: any) => String(p.id) !== currentUserId)?.avatar : null;

                  return (
                    <div key={conv.id} className="relative group/conv">
                      <button
                        onClick={() => handleSelectConversation(conv)}
                        className="w-full p-3 rounded-lg transition-all text-left"
                        style={{
                          backgroundColor: isActive ? localTheme.accentColor + '40' : 'rgba(255, 255, 255, 0.05)',
                          borderLeft: isActive ? `4px solid ${localTheme.accentColor}` : '4px solid transparent'
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                            backgroundColor: localTheme.accentColor + '40'
                          }}>
                            {avatar ? (
                              <img src={resolveAvatar(avatar) || ''} alt={title} className="w-full h-full rounded-full object-cover" />
                            ) : conv.type === 'group' ? (
                              <Users size={24} style={{ color: localTheme.textColor }} />
                            ) : (
                              <PixelatedTurtle size={24} />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h3 className="font-semibold truncate" style={{ color: localTheme.textColor }}>
                                {title}
                              </h3>
                            </div>

                            {conv.lastMessage && conv.lastMessage.from && conv.lastMessage.text && (
                              <p className="text-sm truncate" style={{ color: localTheme.textColor + '99' }}>
                                {String(conv.lastMessage.from)}: {String(conv.lastMessage.text)}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>

                      {/* Timestamp */}
                      {conv.lastMessage && conv.lastMessage.at && (
                        <div className="absolute top-2 right-8 opacity-0 group-hover/conv:opacity-100 transition-opacity pointer-events-none">
                          <span className="text-xs" style={{ color: localTheme.textColor + '60' }}>
                            {formatTimeAgo(conv.lastMessage.at)}
                          </span>
                        </div>
                      )}

                      {/* Three Dots Menu */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover/conv:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowConversationMenu(showConversationMenu === conv.id ? null : conv.id);
                          }}
                          className="p-1 rounded hover:bg-white/10 transition-colors"
                          style={{ color: localTheme.textColor }}
                        >
                          <MoreVertical size={18} />
                        </button>

                        {showConversationMenu === conv.id && (
                          <>
                            <div
                              className="absolute top-full right-0 mt-1 rounded-lg overflow-hidden shadow-lg z-50"
                              style={{
                                backgroundColor: getCardBackground(localTheme as any, {}),
                                borderColor: localTheme.accentColor + '40',
                                border: '1px solid',
                                minWidth: '150px'
                              }}
                            >
                              <button
                                onClick={() => handleHideConversation(conv.id)}
                                className="w-full px-4 py-2 text-left hover:bg-white/10 transition-colors flex items-center gap-2"
                                style={{ color: localTheme.textColor }}
                              >
                                <EyeOff size={16} />
                                Hide
                              </button>
                              <button
                                onClick={() => handleLeaveConversation(conv)}
                                className="w-full px-4 py-2 text-left hover:bg-white/10 transition-colors flex items-center gap-2"
                                style={{ color: '#ef4444' }}
                              >
                                <LogOut size={16} />
                                Leave
                              </button>
                            </div>
                            <div
                              className="fixed inset-0"
                              style={{ zIndex: 40 }}
                              onClick={handleCloseConversationMenu}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Middle Card - Messages (47vw) */}
        <div className="rounded-lg backdrop-blur-sm border overflow-hidden flex flex-col" style={{
          width: '47vw',
          background: getCardBackground(localTheme as any, {}, theme.cardBg),
          borderColor: localTheme.accentColor + '40'
        }}>
          {selectedConversation ? (
            <>
              {/* Message Header */}
              <div className="p-4" style={{ borderBottom: `1px solid ${localTheme.accentColor}40` }}>
                <h2 className="text-xl font-bold" style={{ color: localTheme.textColor }}>
                  {selectedConversation.name || selectedConversation.participants.find((p: any) => String(p.id) !== String(currentUser?.id))?.displayName || 'Conversation'}
                </h2>
              </div>

              {/* Messages Scrollview */}
              <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-4">
                {messages.map((msg, index) => {
                  const sender = selectedConversation.participants.find(p => p.id === msg.senderId);

                  // Show avatar if: first message, sender changed, OR every 10 messages from same sender, OR 10+ minutes since last avatar
                  let showAvatar = index === 0 || messages[index - 1]?.senderId !== msg.senderId;

                  if (!showAvatar && index > 0) {
                    // Count consecutive messages from same sender
                    let consecutiveCount = 1;
                    for (let i = index - 1; i >= 0; i--) {
                      if (messages[i].senderId === msg.senderId) {
                        consecutiveCount++;
                        if (messages[i - 1]?.senderId !== msg.senderId) break;
                      } else {
                        break;
                      }
                    }

                    // Show every 10 messages
                    if (consecutiveCount % 10 === 1 && consecutiveCount > 1) {
                      showAvatar = true;
                    }

                    // Check time difference (10 minutes = 600000 ms)
                    const timeDiff = new Date(msg.timestamp).getTime() - new Date(messages[index - 1].timestamp).getTime();
                    if (timeDiff >= 600000) {
                      showAvatar = true;
                    }
                  }

                  // Check if this is the welcome message and if there are user messages
                  const isWelcomeMessage = msg.senderId === 'system' && msg.senderName === 'EarthSocial';
                  const hasUserMessages = messages.some(m => m.senderId !== 'system');
                  const shouldFadeOut = isWelcomeMessage && hasUserMessages;

                  return (
                    <div
                      key={msg.id}
                      className={`flex gap-4 px-3 py-2 rounded transition-all duration-200 group relative ${shouldFadeOut ? 'opacity-0' : 'opacity-100'}`}
                      style={{
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {/* Profile Picture */}
                      <div className="flex-shrink-0" style={{ width: '48px' }}>
                        {showAvatar && (
                          <Link to={sender?.username ? `/u/${sender.username}` : '#'} className="relative block">
                            <div
                              className={`w-12 h-12 flex items-center justify-center overflow-hidden cursor-pointer transition-opacity hover:opacity-80 ${sender?.profileFrameShape === 'circle' ? 'rounded-full' : 'rounded-lg'}`}
                              style={{
                                border: `2px solid ${localTheme.accentColor}`,
                                backgroundColor: localTheme.accentColor + '20'
                              }}
                            >
                              {sender?.avatar ? (
                                <img src={resolveAvatar(sender.avatar) || ''} alt={sender.displayName} className="w-full h-full object-cover" />
                              ) : (
                                <PixelatedTurtle size={24} />
                              )}
                            </div>
                            {/* Profile Decoration */}
                            {sender?.profileFrameDesign && (
                              <div className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center">
                                {sender.profileFrameDesign === 'coral' && (
                                  <svg width="20" height="20" viewBox="0 0 32 32" className="pixelated">
                                    <g fill={localTheme.accentColor}>
                                      <rect x="14" y="8" width="4" height="4" />
                                      <rect x="12" y="12" width="8" height="4" />
                                    </g>
                                  </svg>
                                )}
                              </div>
                            )}
                          </Link>
                        )}
                      </div>

                      {/* Message Content */}
                      <div className="flex-1 min-w-0">
                        {showAvatar && (
                          <div className="flex items-baseline gap-2 mb-1">
                            <span className="font-bold text-base" style={{ color: localTheme.textColor }}>
                              {msg.senderName}
                            </span>
                            <span className="text-sm" style={{ color: localTheme.textColor + '60' }}>
                              {formatTimeAgo(msg.timestamp)}
                            </span>
                          </div>
                        )}
                        <p className="text-base break-words leading-relaxed" style={{ color: localTheme.textColor + 'DD' }}>
                          {msg.text}
                        </p>
                      </div>

                      {/* Timestamp on hover */}
                      <div className="absolute top-2 right-12 opacity-0 group-hover:opacity-100 transition-opacity text-xs pointer-events-none" style={{ color: localTheme.textColor + '60' }}>
                        {formatFullDateTime(msg.timestamp)}
                      </div>

                      {/* Three Dots Menu for own messages */}
                      {msg.isOwn && (
                        <div className="absolute top-0 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMessageMenu(showMessageMenu === msg.id ? null : msg.id);
                            }}
                            className="p-1 rounded hover:bg-white/10 transition-colors"
                            style={{ color: localTheme.textColor }}
                          >
                            <MoreVertical size={16} />
                          </button>

                          {showMessageMenu === msg.id && (
                            <>
                              <div
                                className="absolute bottom-full right-0 mb-1 rounded-lg overflow-hidden shadow-lg"
                                style={{
                                  backgroundColor: getCardBackground(localTheme as any, {}),
                                  borderColor: localTheme.accentColor + '40',
                                  border: '1px solid',
                                  minWidth: '120px',
                                  zIndex: 9999
                                }}
                              >
                                <button
                                  onClick={async () => {
                                    try {
                                      const response = await fetchWithAuth(`/api/messages/${msg.id}`, {
                                        method: 'DELETE',
                                        credentials: 'include'
                                      });
                                      if (response.ok) {
                                        setMessages(messages.filter(m => m.id !== msg.id));
                                      }
                                    } catch (error) {
                                    }
                                    setShowMessageMenu(null);
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-white/10 transition-colors flex items-center gap-2"
                                  style={{ color: '#ef4444' }}
                                >
                                  <Trash2 size={16} />
                                  Delete
                                </button>
                              </div>
                              <div
                                className="fixed inset-0"
                                style={{ zIndex: 9998 }}
                                onClick={handleCloseMessageMenu}
                              />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input - Discord Style */}
              <div style={{ padding: '20px 40px 40px 40px' }}>
                <div
                  className="rounded-xl overflow-hidden relative"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: `1px solid ${localTheme.accentColor}40`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    marginLeft: '40px',
                    marginRight: '40px'
                  }}
                >
                  <div className="flex items-start" style={{ gap: '14px', padding: '14px 22px' }}>
                    {/* Plus Button */}
                    <button
                      onClick={handleToggleMediaModal}
                      className="flex-shrink-0 rounded-lg hover:bg-white/10 transition-colors"
                      style={{ color: localTheme.textColor + '99', padding: '10px', marginTop: '4px' }}
                      title="Add media"
                    >
                      <Plus size={26} />
                    </button>

                    {/* Textarea Input */}
                    <textarea
                      value={newMessage}
                      onChange={(e) => {
                        if (e.target.value.length <= 2000) {
                          setNewMessage(e.target.value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      placeholder={
                        selectedConversation.type === 'dm'
                          ? `Message @${selectedConversation.participants.find((p: any) => String(p.id) !== String(currentUser?.id))?.username || 'user'}`
                          : selectedConversation.name
                          ? `Message ${selectedConversation.name}`
                          : 'Message Groupchat'
                      }
                      maxLength={2000}
                      className="flex-1 bg-transparent border-none outline-none resize-none"
                      style={{
                        color: localTheme.textColor,
                        fontSize: '16px',
                        lineHeight: '24px',
                        padding: '6px 0',
                        minHeight: '72px',
                        maxHeight: '72px',
                        overflowY: 'auto'
                      }}
                      rows={3}
                    />

                    {/* Emoji Picker Button */}
                    <button
                      onClick={handleToggleEmojiPicker}
                      className="flex-shrink-0 rounded-lg hover:bg-white/10 transition-colors"
                      style={{ color: localTheme.textColor + '99', padding: '10px', marginTop: '4px' }}
                      title="Add emoji"
                    >
                      <Smile size={26} />
                    </button>
                  </div>

                  {/* Character Counter */}
                  {newMessage.length > 1800 && (
                    <div className="px-4 pb-2 text-right">
                      <span className="text-xs" style={{ color: newMessage.length >= 2000 ? '#f87171' : theme.textColor + '99' }}>
                        {newMessage.length}/2000
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              <PixelatedTurtle size={80} />
              <p className="mt-4 text-lg" style={{ color: localTheme.textColor + '99' }}>
                Select a conversation to start messaging
              </p>
            </div>
          )}
        </div>

        {/* Right Card - Profile Card */}
        <div className="flex-1 rounded-lg backdrop-blur-sm border overflow-hidden flex flex-col" style={{
          background: getCardBackground(localTheme as any, {}, theme.cardBg),
          borderColor: localTheme.accentColor + '40'
        }}>
          {selectedConversation && selectedConversation.type === 'dm' ? (
            (() => {
              const recipient = getRecipientUser();
              if (!recipient) return null;

              return (
                <>
                  {/* Top Half - Recipient Profile */}
                  <div className="flex-1 p-6" style={{ borderBottom: `1px solid ${localTheme.accentColor}40` }}>
                    {/* Profile Picture with Frame/Decoration */}
                    <div className="flex flex-col items-center mb-4">
                      <div className="relative">
                        <Link
                          to={`/u/${recipient.username}`}
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          <div
                            className={`w-24 h-24 flex items-center justify-center overflow-hidden ${recipient.profileFrameShape === 'circle' ? 'rounded-full' : 'rounded-lg'}`}
                            style={{
                              border: `4px solid ${localTheme.accentColor}`,
                              backgroundColor: localTheme.accentColor + '20'
                            }}
                          >
                            {recipient.avatar ? (
                              <img src={resolveAvatar(recipient.avatar) || ''} alt={recipient.displayName} className="w-full h-full object-cover" />
                            ) : (
                              <PixelatedTurtle size={48} />
                            )}
                          </div>
                        </Link>

                        {/* Profile Decoration */}
                        {recipient.profileFrameDesign && (
                          <div className="absolute -top-2 -right-2 w-10 h-10 flex items-center justify-center pointer-events-none">
                            {recipient.profileFrameDesign === 'coral' && (
                              <svg width="40" height="40" viewBox="0 0 32 32" className="pixelated">
                                <g fill={localTheme.accentColor}>
                                  <rect x="14" y="8" width="4" height="4" />
                                  <rect x="12" y="12" width="8" height="4" />
                                  <rect x="10" y="16" width="4" height="4" />
                                  <rect x="18" y="16" width="4" height="4" />
                                  <rect x="14" y="20" width="4" height="4" />
                                </g>
                              </svg>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Name */}
                    <h2 className="text-xl font-bold text-center mb-2" style={{ color: localTheme.textColor }}>
                      {recipient.displayName || recipient.username}
                    </h2>

                    {/* Title */}
                    <p className="text-center mb-3" style={{ color: localTheme.textColor + '99' }}>
                      {recipient.profileTitle || 'Earthling'}
                    </p>

                    {/* User Status - Thought Bubble */}
                    {recipient.userStatus && (
                      <div className="thought-bubble">
                        <p className="text-sm text-center whitespace-pre-wrap" style={{ color: localTheme.textColor, lineHeight: '1.5' }}>
                          {recipient.userStatus}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Bottom Half - Current User Profile */}
                  <div className="flex-1 p-6">
                    {/* Profile Picture with Frame/Decoration */}
                    <div className="flex flex-col items-center mb-4">
                      <div className="relative">
                        <Link
                          to="/u"
                          className="cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          <div
                            className={`w-24 h-24 flex items-center justify-center overflow-hidden ${currentUser.profileFrameShape === 'circle' ? 'rounded-full' : 'rounded-lg'}`}
                            style={{
                              border: `4px solid ${localTheme.accentColor}`,
                              backgroundColor: localTheme.accentColor + '20'
                            }}
                          >
                            {currentUser.avatar ? (
                              <img src={resolveAvatar(currentUser.avatar) || ''} alt={currentUser.displayName} className="w-full h-full object-cover" />
                            ) : (
                              <PixelatedTurtle size={48} />
                            )}
                          </div>
                        </Link>

                        {/* Profile Decoration */}
                        {currentUser.profileFrameDesign && (
                          <div className="absolute -top-2 -right-2 w-10 h-10 flex items-center justify-center pointer-events-none">
                            {currentUser.profileFrameDesign === 'coral' && (
                              <svg width="40" height="40" viewBox="0 0 32 32" className="pixelated">
                                <g fill={localTheme.accentColor}>
                                  <rect x="14" y="8" width="4" height="4" />
                                  <rect x="12" y="12" width="8" height="4" />
                                  <rect x="10" y="16" width="4" height="4" />
                                  <rect x="18" y="16" width="4" height="4" />
                                  <rect x="14" y="20" width="4" height="4" />
                                </g>
                              </svg>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Name */}
                    <h2 className="text-xl font-bold text-center mb-2" style={{ color: localTheme.textColor }}>
                      {currentUser.displayName || currentUser.username}
                    </h2>

                    {/* Title */}
                    <p className="text-center mb-3" style={{ color: localTheme.textColor + '99' }}>
                      {currentUser.profileTitle || 'Earthling'}
                    </p>

                    {/* User Status - Thought Bubble (Editable) */}
                    {editingStatus ? (
                      <div className="thought-bubble">
                        <textarea
                          value={statusInput}
                          onChange={(e) => {
                            const lines = e.target.value.split('\n');
                            if (lines.length <= 8) {
                              setStatusInput(e.target.value);
                              // Auto-resize textarea
                              e.target.style.height = 'auto';
                              e.target.style.height = Math.min(e.target.scrollHeight, 168) + 'px';
                            }
                          }}
                          onBlur={handleUpdateStatus}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleUpdateStatus();
                            }
                            if (e.key === 'Enter' && e.shiftKey) {
                              const lines = statusInput.split('\n');
                              if (lines.length >= 8) {
                                e.preventDefault();
                              }
                            }
                            if (e.key === 'Escape') {
                              setEditingStatus(false);
                              setStatusInput(currentUser.userStatus || '');
                            }
                          }}
                          placeholder="What's on your mind?..."
                          maxLength={280}
                          autoFocus
                          rows={1}
                          className="w-full bg-transparent border-none outline-none resize-none text-sm text-center overflow-hidden"
                          style={{
                            color: localTheme.textColor,
                            lineHeight: '1.5',
                            minHeight: '21px',
                            maxHeight: '168px'
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className="thought-bubble cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => {
                          setEditingStatus(true);
                          setStatusInput(currentUser.userStatus || '');
                        }}
                      >
                        <p className="text-sm text-center whitespace-pre-wrap" style={{ color: localTheme.textColor, lineHeight: '1.5' }}>
                          {currentUser.userStatus || 'Click to set your status...'}
                        </p>
                      </div>
                    )}
                  </div>
                </>
              );
            })()
          ) : selectedConversation?.type === 'group' ? (
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: localTheme.textColor }}>
                Group Members
              </h3>
              <div className="space-y-3">
                {selectedConversation.participants.map((participant: any) => (
                  <Link
                    key={participant.id || participant.userId}
                    to={(participant.id === currentUser?.id || participant.userId === currentUser?.id) ? '/u' : `/u/${participant.username}`}
                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                      backgroundColor: localTheme.accentColor + '40'
                    }}>
                      {participant.avatar ? (
                        <img src={resolveAvatar(participant.avatar) || ''} alt={participant.displayName} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <PixelatedTurtle size={20} />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: localTheme.textColor }}>
                        {participant.displayName || participant.username}
                      </p>
                      <p className="text-sm" style={{ color: localTheme.textColor + '99' }}>
                        @{participant.username}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <PixelatedTurtle size={60} />
              <p className="mt-4" style={{ color: localTheme.textColor + '99' }}>
                Select a conversation to view profile
              </p>
            </div>
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      {showNewConversationModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={handleCloseNewConversationModal}
        >
          <div
            className="rounded-lg backdrop-blur-sm border p-6 w-[500px] max-h-[600px] overflow-hidden flex flex-col animate-scale-in"
            style={{
              background: getCardBackground(localTheme as any, {}, theme.cardBg),
              borderColor: localTheme.accentColor + '40',
              animation: 'scaleIn 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <style>{`
              @keyframes scaleIn {
                from {
                  transform: scale(0.9);
                  opacity: 0;
                }
                to {
                  transform: scale(1);
                  opacity: 1;
                }
              }
            `}</style>

            <h2 className="text-2xl font-bold mb-4" style={{ color: localTheme.textColor }}>
              New Conversation
            </h2>

            {/* Search Users */}
            <div className="relative mb-4">
              <Search size={18} className="absolute top-1/2 transform -translate-y-1/2" style={{ color: localTheme.textColor + '60', left: '0.75rem' }} />
              <input
                type="text"
                value={newConvSearchQuery}
                onChange={(e) => setNewConvSearchQuery(e.target.value)}
                placeholder="Search for people..."
                className="w-full pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderColor: localTheme.accentColor + '40',
                  color: localTheme.textColor,
                  paddingLeft: '2.5rem'
                }}
              />
            </div>

            {/* Selected Users */}
            {selectedUsers.length > 0 && (
              <div className="mb-4">
                <p className="text-sm mb-2" style={{ color: localTheme.textColor + '99' }}>
                  Selected ({selectedUsers.length}/20):
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map((user) => (
                    <div
                      key={user.id}
                      className="px-3 py-1 rounded-full flex items-center gap-2"
                      style={{
                        backgroundColor: localTheme.accentColor + '40',
                        color: localTheme.textColor
                      }}
                    >
                      <span className="text-sm">{user.displayName}</span>
                      <button
                        onClick={() => handleToggleUserSelection(user)}
                        className="hover:opacity-70"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Group Chat Title (if more than 1 user selected) */}
            {selectedUsers.length > 1 && (
              <div className="mb-4">
                <label className="block text-sm mb-2" style={{ color: localTheme.textColor + '99' }}>
                  Group Chat Title (optional):
                </label>
                <input
                  type="text"
                  value={groupChatTitle}
                  onChange={(e) => setGroupChatTitle(e.target.value)}
                  placeholder="Enter group name..."
                  className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderColor: localTheme.accentColor + '40',
                    color: localTheme.textColor
                  }}
                />
              </div>
            )}

            {/* Available Users List */}
            <div className="flex-1 overflow-y-auto mb-4">
              <p className="text-sm mb-2" style={{ color: localTheme.textColor + '99' }}>
                Available people:
              </p>
              <div className="space-y-2">
                {availableUsers
                  .filter(user =>
                    !newConvSearchQuery ||
                    user.displayName?.toLowerCase().includes(newConvSearchQuery.toLowerCase()) ||
                    user.username.toLowerCase().includes(newConvSearchQuery.toLowerCase())
                  )
                  .map((user) => {
                    const isSelected = selectedUsers.find(u => u.id === user.id);
                    return (
                      <button
                        key={user.id}
                        onClick={() => handleToggleUserSelection(user)}
                        disabled={!isSelected && selectedUsers.length >= 20}
                        className="w-full p-3 rounded-lg transition-all text-left flex items-center gap-3"
                        style={{
                          backgroundColor: isSelected ? localTheme.accentColor + '40' : 'rgba(255, 255, 255, 0.05)',
                          opacity: (!isSelected && selectedUsers.length >= 20) ? 0.5 : 1,
                          cursor: (!isSelected && selectedUsers.length >= 20) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                          backgroundColor: localTheme.accentColor + '40'
                        }}>
                          <PixelatedTurtle size={20} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold" style={{ color: localTheme.textColor }}>
                            {user.displayName}
                          </p>
                          <p className="text-sm" style={{ color: localTheme.textColor + '99' }}>
                            @{user.username}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{
                            backgroundColor: localTheme.accentColor
                          }}>
                            <span style={{ color: localTheme.textColor }}>✓</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowNewConversationModal(false);
                  setSelectedUsers([]);
                  setGroupChatTitle('');
                  setNewConvSearchQuery('');
                }}
                className="flex-1 px-4 py-2 rounded-lg font-semibold transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleStartConversation}
                disabled={selectedUsers.length === 0}
                className="flex-1 px-4 py-2 rounded-lg font-semibold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor
                }}
              >
                Start Conversation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Emoji Picker Modal */}
      {showEmojiPicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={handleCloseEmojiPicker}
        >
          <div
            className="rounded-lg backdrop-blur-sm border p-6 w-[450px] max-h-[500px] overflow-hidden flex flex-col"
            style={{
              background: getCardBackground(localTheme as any, {}, theme.cardBg),
              borderColor: localTheme.accentColor + '40',
              animation: 'scaleIn 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold" style={{ color: localTheme.textColor }}>
                Choose an Emoji
              </h2>
              <button
                onClick={handleCloseEmojiPicker}
                className="p-2 hover:bg-white/10 rounded transition-colors"
                style={{ color: localTheme.textColor + '99' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-8 gap-2 overflow-y-auto max-h-[400px] p-2">
              {emojis.map((emoji, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setNewMessage(newMessage + emoji);
                    setShowEmojiPicker(false);
                  }}
                  className="text-3xl hover:bg-white/10 rounded-lg p-3 transition-all hover:scale-110"
                  title={emoji}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Media Modal */}
      {showMediaModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          onClick={handleCloseMediaModal}
        >
          <div
            className="rounded-lg backdrop-blur-sm border p-6 w-[500px] flex flex-col"
            style={{
              background: getCardBackground(localTheme as any, {}, theme.cardBg),
              borderColor: localTheme.accentColor + '40',
              animation: 'scaleIn 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold" style={{ color: localTheme.textColor }}>
                Add Media
              </h2>
              <button
                onClick={handleCloseMediaModal}
                className="p-2 hover:bg-white/10 rounded transition-colors"
                style={{ color: localTheme.textColor + '99' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Media Type Selection */}
            <div className="flex gap-3 mb-4">
              <button
                onClick={handleSetMediaTypeImage}
                className="flex-1 px-4 py-2 rounded-lg font-semibold transition-all"
                style={{
                  backgroundColor: mediaType === 'image' ? localTheme.accentColor : 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${mediaType === 'image' ? localTheme.accentColor : localTheme.accentColor + '40'}`
                }}
              >
                Image
              </button>
              <button
                onClick={handleSetMediaTypeVideo}
                className="flex-1 px-4 py-2 rounded-lg font-semibold transition-all"
                style={{
                  backgroundColor: mediaType === 'video' ? localTheme.accentColor : 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${mediaType === 'video' ? localTheme.accentColor : localTheme.accentColor + '40'}`
                }}
              >
                Video
              </button>
            </div>

            {/* Media URL Input */}
            <div className="mb-4">
              <label className="block text-sm mb-2" style={{ color: localTheme.textColor + '99' }}>
                {mediaType === 'image' ? 'Image URL:' : 'Video URL:'}
              </label>
              <input
                type="text"
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                placeholder={`Enter ${mediaType} URL...`}
                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderColor: localTheme.accentColor + '40',
                  color: localTheme.textColor
                }}
              />
            </div>

            {/* Preview */}
            {mediaUrl && (
              <div className="mb-4">
                <p className="text-sm mb-2" style={{ color: localTheme.textColor + '99' }}>
                  Preview:
                </p>
                <div className="rounded-lg overflow-hidden" style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}>
                  {mediaType === 'image' ? (
                    <img src={mediaUrl} alt="Preview" className="w-full h-auto max-h-[200px] object-contain" onError={(e) => { e.currentTarget.src = ''; }} />
                  ) : (
                    <video src={mediaUrl} controls className="w-full h-auto max-h-[200px]" />
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowMediaModal(false);
                  setMediaUrl('');
                }}
                className="flex-1 px-4 py-3 rounded-lg font-semibold transition-all hover:opacity-80"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: localTheme.textColor,
                  border: `1px solid ${localTheme.accentColor}40`
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddMedia}
                disabled={!mediaUrl.trim()}
                className="flex-1 px-4 py-3 rounded-lg font-semibold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: localTheme.accentColor,
                  color: localTheme.textColor
                }}
              >
                Send Media
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
